import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useHealth } from "@/context/HealthContext";
import { HCard, RiskBadge } from "@/components/health-kit";
import { BrainCircuit, ArrowRight, Database, GitBranch, FileOutput } from "lucide-react";

export default function AICore() {
  const { ht, tick } = useHealth();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  useEffect(() => { api.get("/health/analyses").then(({ data }) => setAnalyses(data.slice(0, 8))).catch(() => {}); }, [tick]);

  const col = (title, icon, keys, tone) => (
    <HCard className="p-5">
      <div className={`flex items-center gap-2 mb-4 ${tone}`}>{icon}<h3 className="font-display font-semibold text-sm uppercase tracking-wide">{title}</h3></div>
      <div className="space-y-2">
        {keys.map((k) => <div key={k} className="px-3 py-2 rounded-sm bg-[#050C11] border border-slate-700 text-sm text-slate-200">{ht(k)}</div>)}
      </div>
    </HCard>
  );

  return (
    <div className="space-y-6 ngx-fade-in" data-testid="health-aicore">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-50">{ht("nav_aicore")}</h1>
        <p className="text-sm text-slate-400 mt-1">{ht("aiSupports")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {col(ht("inputs"), <Database className="h-4 w-4" />, ["in_biomarkers", "in_clinical", "in_vitals", "in_care"], "text-cyan-400")}
        {col(ht("processing"), <GitBranch className="h-4 w-4" />, ["pr_data", "pr_pattern", "pr_risk", "pr_pred"], "text-teal-400")}
        {col(ht("outputs"), <FileOutput className="h-4 w-4" />, ["out_summary", "out_trend", "out_risk", "out_ews", "out_info"], "text-amber-400")}
      </div>

      <HCard className="p-5">
        <div className="flex items-center gap-2 mb-4 text-slate-300"><BrainCircuit className="h-4 w-4 text-teal-400" /><h3 className="font-display font-semibold text-sm">{ht("aiAnalyses")}</h3></div>
        <div className="space-y-2" data-testid="aicore-recent">
          {analyses.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-[#050C11] border border-slate-800">
              <span className="font-mono text-sm text-slate-200">{a.patient_id}</span>
              <RiskBadge level={a.risk_level} ht={ht} />
              <span className="text-xs text-slate-500 font-mono">{(a.signals || []).length} {ht("potentialSignals")} · {ht("confidence")} {a.confidence}%</span>
              <span className={`ml-auto text-[11px] font-mono ${a.status === "pending_review" ? "text-amber-400" : "text-emerald-400"}`}>
                {a.status === "pending_review" ? ht("st_requires_review") : ht("st_reviewed")}
              </span>
              {a.status === "pending_review" && (
                <button onClick={() => navigate("/health/review")} data-testid={`aicore-review-${a.patient_id}`} className="text-teal-400 hover:text-teal-300"><ArrowRight className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
      </HCard>
    </div>
  );
}
